/**
 * Feed ordering: which column the Feed is sorted by, and how to compare two
 * cards on it.
 *
 * The Feed used to offer two orderings — Hot and New — and both were decided
 * server-side by what the fetch returned. This is the other axis: the visitor
 * picking a column. It sits beside the Hot/New tabs rather than replacing them,
 * because "featured" here means "whatever order the tab handed us", and that
 * is still the default.
 *
 * Deliberately free of the DOM and of the network, like console-cards.js: it
 * takes the card dicts toCard() already built and returns them in an order.
 * That is what lets it sort a feed the server rendered and a feed the client
 * fetched with the same comparator, and it is why the sort keys live in
 * console-cards.js::ROW_COLUMNS — the header that offers a column and the
 * comparator that can serve it are declared once.
 *
 * The stored choice is per-browser, like the floor and the Tiles/Rows view. It
 * is not in the URL: the Feed is edge-cached and one body serves everyone (see
 * cache_middleware), so a sort in the query string would be a cache key that
 * multiplies the most-cached page on the site by seven.
 */

const KEY = 'uf_feed_sort';

/** No sort: the order the Hot/New tab handed us. */
export const DEFAULT_SORT = { key: 'featured', dir: 'desc' };

/**
 * Which way a column runs when you first click it.
 *
 * Not all descending: the useful end of "price" and of "vs typical ask" is the
 * low one — cheapest first, furthest below the typical ask first — while the
 * useful end of a trust score or a feedback count is the high one. A column
 * that opened at the wrong end would cost every visitor a second click.
 */
const FIRST_DIR = {
  name: 'asc',
  price: 'asc',
  delta: 'asc',
  seller: 'desc',
  trust: 'desc',
  featured: 'desc',
};

export const SORT_KEYS = Object.keys(FIRST_DIR);

/** The direction a column opens in. */
export function firstDirection(key) {
  return FIRST_DIR[key] || 'desc';
}

let current = null;
const listeners = new Set();

function read() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { ...DEFAULT_SORT };
    const [key, dir] = String(raw).split(':');
    if (!SORT_KEYS.includes(key)) return { ...DEFAULT_SORT };
    return { key, dir: dir === 'asc' ? 'asc' : 'desc' };
  } catch {
    // Private mode, or storage refused. A feed in the default order is a
    // working feed; a throw here would take the render with it.
    return { ...DEFAULT_SORT };
  }
}

/** The sort in force. */
export function getSort() {
  if (!current) current = read();
  return { ...current };
}

/** Set the sort and tell everyone. A repeat of what is already set is a no-op. */
export function setSort(key, dir) {
  const next = {
    key: SORT_KEYS.includes(key) ? key : DEFAULT_SORT.key,
    dir: dir === 'asc' ? 'asc' : 'desc',
  };
  const now = getSort();
  if (now.key === next.key && now.dir === next.dir) return now;
  current = next;
  try {
    localStorage.setItem(KEY, `${next.key}:${next.dir}`);
  } catch { /* see read() */ }
  for (const listener of listeners) listener({ ...next });
  return { ...next };
}

/**
 * What a click on a column head means: open it at its natural end, or, if it
 * is already the sorted column, turn it around.
 */
export function toggleSort(key) {
  const now = getSort();
  return setSort(key, now.key === key ? (now.dir === 'asc' ? 'desc' : 'asc') : firstDirection(key));
}

export function onSortChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * The number (or string) a card sorts on for a column, or null when the card
 * has nothing to offer that column.
 *
 * Null is not zero. A listing with no MarketPriceStat has no delta and a seller
 * we hold no feedback for has no trust score; both sort to the end whichever
 * way the column runs, rather than to the top as a zero and reading like the
 * best answer.
 */
export function sortValue(card, key) {
  switch (key) {
    case 'name':
      return card.name ? String(card.name).toLowerCase() : null;
    case 'price':
      return Number.isFinite(card.price_value) ? card.price_value : null;
    case 'delta':
      // Signed, so "12% below" sorts under "3% below" and both under "5%
      // above". The card's own `pct` is a magnitude — it has the arrow to
      // carry the direction and a comparator does not.
      if (!card.delta) return null;
      return card.delta.up ? card.delta.pct : -card.delta.pct;
    case 'seller':
      // Feedback percentage, with the review count folded in as the tiebreak:
      // 99.8% over 40,000 sales and 99.8% over 12 are not the same seller, and
      // the second number is the only thing that says so. Capped at a million
      // reviews so the tiebreak can never outweigh a whole feedback point.
      if (card.seller_feedback === null || card.seller_feedback === undefined) return null;
      return card.seller_feedback
        + Math.min(card.seller_reviews || 0, 1e6) / 1e7;
    case 'trust':
      return card.trust ? card.trust.score : null;
    default:
      return null;
  }
}

/**
 * `cards`, ordered. The input is never mutated — the caller's array is the
 * unsorted feed and it has to stay that way for the next sort.
 */
export function sortCards(cards, sort = getSort()) {
  const list = [...cards];
  if (!sort || sort.key === 'featured') return list;
  const sign = sort.dir === 'asc' ? 1 : -1;
  return list
    // A decorate/sort/undecorate, because Array#sort is only stable within
    // equal keys and "equal" here includes every card that has no value at all.
    .map((card, index) => ({ card, index, value: sortValue(card, sort.key) }))
    .sort((a, b) => {
      if (a.value === null && b.value === null) return a.index - b.index;
      if (a.value === null) return 1;   // absent sorts last, both directions
      if (b.value === null) return -1;
      if (a.value === b.value) return a.index - b.index;
      return (a.value < b.value ? -1 : 1) * sign;
    })
    .map((entry) => entry.card);
}

/** Test seam: forget the resolved value so the next getSort() re-reads. */
export function _reset() {
  current = null;
  listeners.clear();
}
